import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink } from "react-router-dom";

import { SubHeader } from "../../../utils/componentes-utiles/SubHeader";
import {
	actualizarDelegacionesConfig,
	eliminarDelegacionesConfig,
	getDelegacionesConfig,
	importarDelegacionesConfig,
} from "../../../services/apis";
import { formatDateTimeEs } from "../../../utils/formatters";

const INPUT_ACCEPT = ".json,application/json,text/json";

const buildEmptyImportResult = () => ({
	archivo: "",
	campana: null,
	filas_validas: 0,
	filas_descartadas: 0,
	clientes_insertados: 0,
	clientes_actualizados: 0,
	delegaciones_implicadas: 0,
	delegaciones_autoasignadas: 0,
	warnings: [],
});

const buildDelegacionForm = () => ({
	id: null,
	nombre: "",
});

const buildDelegacionAssignments = (items = []) =>
	Object.fromEntries(
		(Array.isArray(items) ? items : []).map((delegacion) => [
			String(delegacion?.id || ""),
			delegacion?.id_campana_default ? String(delegacion.id_campana_default) : "",
		])
	);

const buildClienteForm = () => ({
	id: null,
	nombre: "",
	ref_cliente_maestro: "",
	regimen: "",
	id_delegacion: "",
	id_campana: "",
});

const normalizeSearchText = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

export const Delegaciones = () => {
	const fileInputRef = useRef(null);

	const [loading, setLoading] = useState(true);
	const [importing, setImporting] = useState(false);
	const [saving, setSaving] = useState(false);
	const [dragActive, setDragActive] = useState(false);

	const [delegaciones, setDelegaciones] = useState([]);
	const [campanas, setCampanas] = useState([]);
	const [clientes, setClientes] = useState([]);
	const [canManage, setCanManage] = useState(false);
	const [summary, setSummary] = useState({
		total_delegaciones: 0,
		total_delegaciones_sin_campana_default: 0,
		total_clientes_relacionados: 0,
		total_clientes_sin_delegacion: 0,
		total_clientes_sin_campana: 0,
	});

	const [selectedCampaign, setSelectedCampaign] = useState("");
	const [clientCampaignFilter, setClientCampaignFilter] = useState("");
	const [clientDelegacionFilter, setClientDelegacionFilter] = useState("");
	const [clientSearch, setClientSearch] = useState("");
	const [selectedFile, setSelectedFile] = useState(null);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [importResult, setImportResult] = useState(buildEmptyImportResult());
	const [delegacionDialogOpen, setDelegacionDialogOpen] = useState(false);
	const [clienteDialogOpen, setClienteDialogOpen] = useState(false);
	const [delegacionForm, setDelegacionForm] = useState(buildDelegacionForm());
	const [clienteForm, setClienteForm] = useState(buildClienteForm());
	const [delegacionAssignments, setDelegacionAssignments] = useState({});
	const [dialogError, setDialogError] = useState("");

	const loadDelegaciones = useCallback(async () => {
		setLoading(true);
		setError("");

		try {
			const response = await getDelegacionesConfig();

			if (response?.success !== true) {
				setDelegaciones([]);
				setCampanas([]);
				setClientes([]);
				setSummary({
					total_delegaciones: 0,
					total_delegaciones_sin_campana_default: 0,
					total_clientes_relacionados: 0,
					total_clientes_sin_delegacion: 0,
					total_clientes_sin_campana: 0,
				});
				setError(response?.message || "No se pudieron cargar las delegaciones.");
				return;
			}

			const payload = response?.data || {};
			const availableCampaigns = Array.isArray(payload?.campanas) ? payload.campanas : [];

			setDelegaciones(Array.isArray(payload?.delegaciones) ? payload.delegaciones : []);
			setCampanas(availableCampaigns);
			setClientes(Array.isArray(payload?.clientes) ? payload.clientes : []);
			setCanManage(payload?.can_manage === true);
			setSummary(
				payload?.summary || {
					total_delegaciones: 0,
					total_delegaciones_sin_campana_default: 0,
					total_clientes_relacionados: 0,
					total_clientes_sin_delegacion: 0,
					total_clientes_sin_campana: 0,
				}
			);
			setSelectedCampaign((current) => {
				if (
					current &&
					availableCampaigns.some((campana) => String(campana?.id) === String(current))
				) {
					return current;
				}

				return "";
			});
		} catch (loadError) {
			console.error("Error cargando delegaciones:", loadError);
			setDelegaciones([]);
			setCampanas([]);
			setClientes([]);
			setError("No se pudo cargar la configuración de delegaciones.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadDelegaciones();
	}, [loadDelegaciones]);

	useEffect(() => {
		setDelegacionAssignments(buildDelegacionAssignments(delegaciones));
	}, [delegaciones]);

	const stats = useMemo(
		() => [
			{
				label: "Delegaciones",
				value: Number(summary?.total_delegaciones || 0),
				helper: "Configuradas en sistema",
			},
			{
				label: "Deleg. sin campaña",
				value: Number(summary?.total_delegaciones_sin_campana_default || 0),
				helper: "Pendientes de asignar",
			},
			{
				label: "Clientes ligados",
				value: Number(summary?.total_clientes_relacionados || 0),
				helper: "Con delegación asignada",
			},
			{
				label: "Sin delegación",
				value: Number(summary?.total_clientes_sin_delegacion || 0),
				helper: "Pendientes de clasificar",
			},
			{
				label: "Sin campaña",
				value: Number(summary?.total_clientes_sin_campana || 0),
				helper: "Ligados sin campaña",
			},
		],
		[summary]
	);

	const campaignFilterOptions = useMemo(
		() =>
			[...campanas].sort((left, right) =>
				String(left?.nombre || "").localeCompare(String(right?.nombre || ""), "es")
			),
		[campanas]
	);

	const delegacionFilterOptions = useMemo(
		() =>
			[...delegaciones].sort((left, right) =>
				String(left?.nombre || "").localeCompare(String(right?.nombre || ""), "es")
			),
		[delegaciones]
	);

	const filteredClientes = useMemo(() => {
		const normalizedSearch = normalizeSearchText(clientSearch);

		return clientes.filter((cliente) => {
			const matchesCampaign =
				clientCampaignFilter === "" ||
				Number(cliente?.id_campana || 0) === Number(clientCampaignFilter);
			const matchesDelegacion =
				clientDelegacionFilter === "" ||
				Number(cliente?.id_delegacion || 0) === Number(clientDelegacionFilter);
			const matchesSearch =
				normalizedSearch === "" ||
				[
					cliente?.nombre,
					cliente?.ref_cliente_maestro,
					cliente?.id_clienteG,
					cliente?.regimen,
					cliente?.delegacion_nombre,
					cliente?.campana_nombre,
				].some((field) => normalizeSearchText(field).includes(normalizedSearch));

			return matchesCampaign && matchesDelegacion && matchesSearch;
		});
	}, [clientes, clientCampaignFilter, clientDelegacionFilter, clientSearch]);

	const activeCampaignExists = campanas.some((campana) => campana?.activa === true);
	const canImport = selectedFile instanceof File && activeCampaignExists;

	useEffect(() => {
		if (
			clientCampaignFilter !== "" &&
			!campanas.some((campana) => String(campana?.id) === String(clientCampaignFilter))
		) {
			setClientCampaignFilter("");
		}
	}, [campanas, clientCampaignFilter]);

	useEffect(() => {
		if (
			clientDelegacionFilter !== "" &&
			!delegaciones.some(
				(delegacion) => String(delegacion?.id) === String(clientDelegacionFilter)
			)
		) {
			setClientDelegacionFilter("");
		}
	}, [delegaciones, clientDelegacionFilter]);

	const handleFileSelection = (fileList) => {
		const file = Array.from(fileList || []).find((candidate) => candidate instanceof File) || null;

		if (!file) {
			return;
		}

		const fileName = String(file?.name || "").toLowerCase();
		if (!fileName.endsWith(".json")) {
			setError("Selecciona un archivo JSON válido.");
			return;
		}

		setSelectedFile(file);
		setError("");
		setSuccess("");
		setImportResult(buildEmptyImportResult());
	};

	const openFilePicker = () => {
		if (importing) return;
		fileInputRef.current?.click();
	};

	const handleImport = async () => {
		if (!selectedFile) {
			setError("Debes seleccionar un archivo JSON.");
			return;
		}

		if (false && !Number(selectedCampaign || 0)) {
			setError("Debes seleccionar una campaña antes de importar.");
			return;
		}

		setImporting(true);
		setError("");
		setSuccess("");

		try {
			const response = await importarDelegacionesConfig({
				archivo: selectedFile,
				id_campana: Number(selectedCampaign),
			});

			if (response?.success !== true) {
				setError(response?.message || "No se pudo importar el archivo.");
				return;
			}

			const payload = response?.data || {};
			setDelegaciones(Array.isArray(payload?.delegaciones) ? payload.delegaciones : []);
			setCampanas(Array.isArray(payload?.campanas) ? payload.campanas : []);
			setClientes(Array.isArray(payload?.clientes) ? payload.clientes : []);
			setCanManage(payload?.can_manage === true);
			setSummary(
				payload?.summary || {
					total_delegaciones: 0,
					total_delegaciones_sin_campana_default: 0,
					total_clientes_relacionados: 0,
					total_clientes_sin_delegacion: 0,
					total_clientes_sin_campana: 0,
				}
			);
			setImportResult(payload?.import || buildEmptyImportResult());
			setSuccess(response?.message || "Importación completada.");
			setSelectedFile(null);
		} catch (importError) {
			console.error("Error importando delegaciones:", importError);
			setError(importError?.response?.data?.message || "No se pudo importar el archivo.");
		} finally {
			setImporting(false);
		}
	};

	const closeDelegacionDialog = () => {
		if (saving) return;
		setDelegacionDialogOpen(false);
		setDelegacionForm(buildDelegacionForm());
		setDialogError("");
	};

	const closeClienteDialog = () => {
		if (saving) return;
		setClienteDialogOpen(false);
		setClienteForm(buildClienteForm());
		setDialogError("");
	};

	const openDelegacionEditor = (delegacion) => {
		setDelegacionForm({
			id: Number(delegacion?.id || 0) || null,
			nombre: String(delegacion?.nombre || ""),
		});
		setDialogError("");
		setDelegacionDialogOpen(true);
	};

	const openClienteEditor = (cliente) => {
		setClienteForm({
			id: Number(cliente?.id || 0) || null,
			nombre: String(cliente?.nombre || ""),
			ref_cliente_maestro: String(
				cliente?.ref_cliente_maestro || cliente?.id_clienteG || ""
			),
			regimen: String(cliente?.regimen || ""),
			id_delegacion: String(cliente?.id_delegacion || ""),
			id_campana: String(cliente?.id_campana || ""),
		});
		setDialogError("");
		setClienteDialogOpen(true);
	};

	const handleDelegacionAssignmentChange = (delegacionId, value) => {
		setDelegacionAssignments((current) => ({
			...current,
			[String(delegacionId)]: value,
		}));
	};

	const handleSaveDelegacionAssignment = async (delegacion) => {
		if (!delegacion?.id) {
			return;
		}

		setSaving(true);
		setDialogError("");
		setError("");
		setSuccess("");

		try {
			const response = await actualizarDelegacionesConfig({
				entity: "delegacion",
				id: delegacion.id,
				nombre: String(delegacion?.nombre || "").trim(),
				id_campana_default: Number(
					delegacionAssignments[String(delegacion.id)] || 0
				),
			});

			if (response?.success !== true) {
				setError(response?.message || "No se pudo guardar la campaña de la delegaciÃ³n.");
				return;
			}

			const payload = response?.data || {};
			setDelegaciones(Array.isArray(payload?.delegaciones) ? payload.delegaciones : []);
			setCampanas(Array.isArray(payload?.campanas) ? payload.campanas : []);
			setClientes(Array.isArray(payload?.clientes) ? payload.clientes : []);
			setCanManage(payload?.can_manage === true);
			setSummary(
				payload?.summary || {
					total_delegaciones: 0,
					total_delegaciones_sin_campana_default: 0,
					total_clientes_relacionados: 0,
					total_clientes_sin_delegacion: 0,
					total_clientes_sin_campana: 0,
				}
			);
			setSuccess(
				response?.message || "Campaña por defecto actualizada correctamente."
			);
		} catch (saveError) {
			console.error("Error guardando campaña por delegaciÃ³n:", saveError);
			setError(
				saveError?.response?.data?.message ||
					"No se pudo guardar la campaña de la delegaciÃ³n."
			);
		} finally {
			setSaving(false);
		}
	};

	const handleSaveDelegacion = async () => {
		if (!delegacionForm.nombre.trim()) {
			setDialogError("Debes indicar un nombre para la delegación.");
			return;
		}

		setSaving(true);
		setDialogError("");
		setError("");
		setSuccess("");

		try {
			const response = await actualizarDelegacionesConfig({
				entity: "delegacion",
				id: delegacionForm.id,
				nombre: delegacionForm.nombre.trim(),
			});

			if (response?.success !== true) {
				setDialogError(response?.message || "No se pudo guardar la delegación.");
				return;
			}

			const payload = response?.data || {};
			setDelegaciones(Array.isArray(payload?.delegaciones) ? payload.delegaciones : []);
			setCampanas(Array.isArray(payload?.campanas) ? payload.campanas : []);
			setClientes(Array.isArray(payload?.clientes) ? payload.clientes : []);
			setCanManage(payload?.can_manage === true);
			setSummary(
				payload?.summary || {
					total_delegaciones: 0,
					total_clientes_relacionados: 0,
					total_clientes_sin_delegacion: 0,
					total_clientes_sin_campana: 0,
				}
			);
			setSuccess(response?.message || "Delegación actualizada correctamente.");
			closeDelegacionDialog();
		} catch (saveError) {
			console.error("Error guardando delegación:", saveError);
			setDialogError(
				saveError?.response?.data?.message || "No se pudo guardar la delegación."
			);
		} finally {
			setSaving(false);
		}
	};

	const handleSaveCliente = async () => {
		if (!clienteForm.nombre.trim()) {
			setDialogError("Debes indicar un nombre para el cliente.");
			return;
		}

		if (!Number(clienteForm.id_delegacion || 0)) {
			setDialogError("Debes seleccionar una delegación.");
			return;
		}

		if (!Number(clienteForm.id_campana || 0)) {
			setDialogError("Debes seleccionar una campaña.");
			return;
		}

		setSaving(true);
		setDialogError("");
		setError("");
		setSuccess("");

		try {
			const response = await actualizarDelegacionesConfig({
				entity: "cliente",
				id: clienteForm.id,
				nombre: clienteForm.nombre.trim(),
				ref_cliente_maestro: clienteForm.ref_cliente_maestro.trim(),
				regimen: clienteForm.regimen.trim(),
				id_delegacion: Number(clienteForm.id_delegacion || 0),
				id_campana: Number(clienteForm.id_campana || 0),
			});

			if (response?.success !== true) {
				setDialogError(response?.message || "No se pudo guardar el cliente.");
				return;
			}

			const payload = response?.data || {};
			setDelegaciones(Array.isArray(payload?.delegaciones) ? payload.delegaciones : []);
			setCampanas(Array.isArray(payload?.campanas) ? payload.campanas : []);
			setClientes(Array.isArray(payload?.clientes) ? payload.clientes : []);
			setCanManage(payload?.can_manage === true);
			setSummary(
				payload?.summary || {
					total_delegaciones: 0,
					total_clientes_relacionados: 0,
					total_clientes_sin_delegacion: 0,
					total_clientes_sin_campana: 0,
				}
			);
			setSuccess(response?.message || "Cliente actualizado correctamente.");
			closeClienteDialog();
		} catch (saveError) {
			console.error("Error guardando cliente:", saveError);
			setDialogError(
				saveError?.response?.data?.message || "No se pudo guardar el cliente."
			);
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteDelegacion = async (delegacion) => {
		if (!delegacion?.id) {
			return;
		}

		const confirmed = window.confirm(
			`¿Seguro que quieres eliminar la delegación "${delegacion.nombre}"?`
		);

		if (!confirmed) {
			return;
		}

		setSaving(true);
		setError("");
		setSuccess("");

		try {
			const response = await eliminarDelegacionesConfig({
				entity: "delegacion",
				id: delegacion.id,
			});

			if (response?.success !== true) {
				setError(response?.message || "No se pudo eliminar la delegación.");
				return;
			}

			const payload = response?.data || {};
			setDelegaciones(Array.isArray(payload?.delegaciones) ? payload.delegaciones : []);
			setCampanas(Array.isArray(payload?.campanas) ? payload.campanas : []);
			setClientes(Array.isArray(payload?.clientes) ? payload.clientes : []);
			setCanManage(payload?.can_manage === true);
			setSummary(
				payload?.summary || {
					total_delegaciones: 0,
					total_clientes_relacionados: 0,
					total_clientes_sin_delegacion: 0,
					total_clientes_sin_campana: 0,
				}
			);
			setSuccess(response?.message || "Delegación eliminada correctamente.");
		} catch (deleteError) {
			console.error("Error eliminando delegación:", deleteError);
			setError(
				deleteError?.response?.data?.message ||
					"No se pudo eliminar la delegación."
			);
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteCliente = async (cliente) => {
		if (!cliente?.id) {
			return;
		}

		const confirmed = window.confirm(
			`¿Seguro que quieres eliminar a "${cliente.nombre}" del módulo de delegaciones?`
		);

		if (!confirmed) {
			return;
		}

		setSaving(true);
		setError("");
		setSuccess("");

		try {
			const response = await eliminarDelegacionesConfig({
				entity: "cliente",
				id: cliente.id,
			});

			if (response?.success !== true) {
				setError(response?.message || "No se pudo eliminar el cliente.");
				return;
			}

			const payload = response?.data || {};
			setDelegaciones(Array.isArray(payload?.delegaciones) ? payload.delegaciones : []);
			setCampanas(Array.isArray(payload?.campanas) ? payload.campanas : []);
			setClientes(Array.isArray(payload?.clientes) ? payload.clientes : []);
			setCanManage(payload?.can_manage === true);
			setSummary(
				payload?.summary || {
					total_delegaciones: 0,
					total_clientes_relacionados: 0,
					total_clientes_sin_delegacion: 0,
					total_clientes_sin_campana: 0,
				}
			);
			setSuccess(
				response?.message || "Cliente eliminado del módulo de delegaciones."
			);
		} catch (deleteError) {
			console.error("Error eliminando cliente:", deleteError);
			setError(
				deleteError?.response?.data?.message || "No se pudo eliminar el cliente."
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }} sx={{ px: 2, pb: 6 }}>
			<SubHeader titulo="Delegaciones" />

			<input
				ref={fileInputRef}
				type="file"
				accept={INPUT_ACCEPT}
				style={{ display: "none" }}
				onChange={(event) => {
					handleFileSelection(event.target.files);
					event.target.value = "";
				}}
			/>

			<Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="center">
				<Button
					variant="text"
					startIcon={<ArrowBackIcon />}
					component={RouterLink}
					to="/admin"
				>
					Volver a configuración
				</Button>
				<Button
					variant="outlined"
					startIcon={<RefreshIcon />}
					onClick={loadDelegaciones}
					disabled={loading || importing}
				>
					Recargar
				</Button>
			</Grid>

			<Alert severity="info" sx={{ mb: 2 }}>
				Este módulo crea y mantiene las delegaciones a partir de un JSON maestro.
				La importación asigna cada cliente a una delegación y a una campaña,
				rellenando `clientes.id_delegacion` y `clientes.id_campana`.
			</Alert>

			<Grid container spacing={2} sx={{ mb: 2 }}>
				{stats.map((item) => (
					<Grid key={item.label} size={{ xs: 12, sm: 6, lg: 3 }}>
						<Paper
							variant="outlined"
							sx={{
								p: 2,
								height: "100%",
								borderRadius: 2,
							}}
						>
							<Typography variant="overline" color="text.secondary">
								{item.label}
							</Typography>
							<Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
								{item.value}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{item.helper}
							</Typography>
						</Paper>
					</Grid>
				))}
			</Grid>

			{!!error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
					{error}
				</Alert>
			)}

			{!!success && (
				<Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
					{success}
				</Alert>
			)}

			{importResult?.archivo ? (
				<Alert severity={importResult?.warnings?.length > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
						<Typography variant="body2" sx={{ fontWeight: 700 }}>
							Última importación: {importResult.archivo}
						</Typography>
						<Typography variant="body2">
							{importResult.clientes_insertados} cliente(s) insertado(s),{" "}
							{importResult.clientes_actualizados} actualizado(s) y{" "}
							{importResult.filas_descartadas} fila(s) descartada(s).
						</Typography>
						{importResult?.campana?.nombre && (
							<Typography variant="body2">
								Campaña de respaldo usada: {importResult.campana.nombre}
							</Typography>
						)}
						{Number(importResult?.delegaciones_autoasignadas || 0) > 0 && (
							<Typography variant="body2">
								Se autoasignó la campaña de respaldo a{" "}
								{importResult.delegaciones_autoasignadas} delegación(es) que
								aún no tenían campaña.
							</Typography>
						)}
						{Array.isArray(importResult?.warnings) && importResult.warnings.length > 0 && (
							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
								{importResult.warnings.slice(0, 6).map((warning, index) => (
									<Typography key={`delegacion-warning-${index}`} variant="caption">
										{warning}
									</Typography>
								))}
								{importResult.warnings.length > 6 && (
									<Typography variant="caption">
										+ {importResult.warnings.length - 6} aviso(s) más.
									</Typography>
								)}
							</Box>
						)}
					</Box>
				</Alert>
			) : null}

			{/* <Paper
				variant="outlined"
				sx={{
					p: 2,
					mb: 3,
					borderRadius: 2,
				}}
			>
				<Grid container spacing={2} alignItems="stretch">
					<Grid size={{ xs: 12, md: 4 }}>
						<Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<AccountTreeIcon color="primary" />
							Importar maestro
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							El JSON debe incluir al menos `Ref_Cte`, `Nombre`, `Delegacion` y
							`Regimen`. La campaña se selecciona aquí y se aplica a todos los
							clientes importados en esta carga.
						</Typography>
					</Grid>

					<Grid size={{ xs: 12, md: 8 }}>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, md: 5 }}>
								<FormControl fullWidth size="small" disabled={importing || campanas.length === 0}>
									<InputLabel id="delegaciones-campana-label">Campaña destino</InputLabel>
									<Select
										labelId="delegaciones-campana-label"
										label="Campaña destino"
										value={selectedCampaign}
										onChange={(event) => setSelectedCampaign(event.target.value)}
									>
										<MenuItem value="">
											<em>Sin respaldo</em>
										</MenuItem>
										{campanas.map((campana) => (
											<MenuItem key={`delegaciones-campana-${campana.id}`} value={String(campana.id)}>
												{campana.nombre}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid size={{ xs: 12, md: 7 }}>
								<Paper
									variant="outlined"
									role="button"
									tabIndex={0}
									onClick={openFilePicker}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											openFilePicker();
										}
									}}
									onDragEnter={(event) => {
										event.preventDefault();
										setDragActive(true);
									}}
									onDragOver={(event) => {
										event.preventDefault();
										setDragActive(true);
									}}
									onDragLeave={(event) => {
										event.preventDefault();
										if (event.currentTarget === event.target) {
											setDragActive(false);
										}
									}}
									onDrop={(event) => {
										event.preventDefault();
										setDragActive(false);
										handleFileSelection(event.dataTransfer?.files);
									}}
									sx={{
										p: 2,
										minHeight: 120,
										borderRadius: 2,
										borderStyle: "dashed",
										borderWidth: 2,
										borderColor: dragActive ? "primary.main" : "divider",
										bgcolor: dragActive ? "primary.50" : "grey.50",
										cursor: importing ? "progress" : "pointer",
										transition: "all 0.2s ease",
									}}
								>
									<Box
										sx={{
											height: "100%",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: 2,
											flexDirection: { xs: "column", sm: "row" },
										}}
									>
										<Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
											<CloudUploadOutlinedIcon color={dragActive ? "primary" : "action"} />
											<Box>
												<Typography variant="subtitle2">
													Arrastra el JSON aquí o haz click para seleccionarlo
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Se importará contra la campaña seleccionada.
												</Typography>
												{selectedFile && (
													<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
														Archivo listo: {selectedFile.name}
													</Typography>
												)}
											</Box>
										</Box>

										<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
											<Button
												size="small"
												variant="outlined"
												onClick={(event) => {
													event.stopPropagation();
													openFilePicker();
												}}
												disabled={importing}
											>
												Seleccionar
											</Button>
											<Button
												size="small"
												variant="contained"
												onClick={(event) => {
													event.stopPropagation();
													void handleImport();
												}}
												disabled={!canImport || importing}
											>
												{importing ? "Importando..." : "Importar"}
											</Button>
										</Box>
									</Box>
								</Paper>
							</Grid>
						</Grid>

						{campanas.length === 0 && (
							<Alert severity="warning" sx={{ mt: 2 }}>
								No hay campañas disponibles. Crea primero una campaña antes de importar
								clientes por delegación.
							</Alert>
						)}
					</Grid>
				</Grid>
			</Paper> */}

			<Grid container spacing={2}>
				<Grid size={{ xs: 12, lg: 5 }}>
					<Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
						<Box
							sx={{
								px: 2,
								py: 1.5,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								gap: 1,
								bgcolor: "grey.50",
							}}
						>
							<Box>
								<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
									Delegaciones registradas
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Resumen de clientes clasificados por delegación.
								</Typography>
							</Box>
							<Chip size="small" variant="outlined" label={`${delegaciones.length} delegación(es)`} />
						</Box>

						{loading ? (
							<Box
								sx={{
									minHeight: 220,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<CircularProgress size={20} />
							</Box>
						) : delegaciones.length === 0 ? (
							<Box sx={{ p: 2.5 }}>
								<Typography variant="body2" color="text.secondary">
									Aún no hay delegaciones importadas.
								</Typography>
							</Box>
						) : (
							<Box>
								{delegaciones.map((delegacion) => (
									<Box
										key={`delegacion-item-${delegacion.id}`}
										sx={{
											px: 2,
											py: 1.5,
											borderTop: "1px solid",
											borderColor: "divider",
										}}
									>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "flex-start",
												gap: 1.5,
												flexWrap: "wrap",
											}}
										>
											<Box>
												<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
													{delegacion.nombre}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Actualizada {formatDateTimeEs(delegacion.fecha_actualizacion)}
												</Typography>
												<Box
													sx={{
														mt: 1.25,
														display: "flex",
														flexDirection: { xs: "column", sm: "row" },
														alignItems: { xs: "stretch", sm: "center" },
														gap: 1,
														flexWrap: "wrap",
													}}
												>
													<Chip
														size="small"
														color={
															delegacion?.campana_default_nombre
																? "primary"
																: "default"
														}
														variant={
															delegacion?.campana_default_nombre
																? "filled"
																: "outlined"
														}
														label={
															delegacion?.campana_default_nombre
																? `Por defecto: ${delegacion.campana_default_nombre}`
																: "Sin campaña por defecto"
														}
													/>
													{canManage && (
														<FormControl
															size="small"
															sx={{ minWidth: { xs: "100%", sm: 230 } }}
															disabled={saving || campaignFilterOptions.length === 0}
														>
															<InputLabel id={`delegacion-default-campaign-${delegacion.id}`}>
																Campaña por defecto
															</InputLabel>
															<Select
																labelId={`delegacion-default-campaign-${delegacion.id}`}
																label="Campaña por defecto"
																value={
																	delegacionAssignments[String(delegacion.id)] ?? ""
																}
																onChange={(event) =>
																	handleDelegacionAssignmentChange(
																		delegacion.id,
																		event.target.value
																	)
																}
															>
																<MenuItem value="">
																	<em>Sin asignar</em>
																</MenuItem>
																{campaignFilterOptions.map((campana) => (
																	<MenuItem
																		key={`delegacion-default-campana-${delegacion.id}-${campana.id}`}
																		value={String(campana.id)}
																	>
																		{campana.nombre}
																	</MenuItem>
																))}
															</Select>
														</FormControl>
													)}
													{canManage && (
														<Button
															size="small"
															variant="contained"
															onClick={() =>
																void handleSaveDelegacionAssignment(delegacion)
															}
															disabled={
																saving ||
																String(delegacion?.id_campana_default || "") ===
																	String(
																		delegacionAssignments[String(delegacion.id)] ?? ""
																	)
															}
														>
															Guardar campaña
														</Button>
													)}
												</Box>
											</Box>

											<Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
												<Chip size="small" color="success" label={`${delegacion.total_clientes} cliente(s)`} />
												<Chip
													size="small"
													variant="outlined"
													label={`${delegacion.total_clientes_con_campana} con campaña`}
												/>
												{canManage && (
													<IconButton
														size="small"
														onClick={() => openDelegacionEditor(delegacion)}
														disabled={saving}
													>
														<EditOutlinedIcon fontSize="small" />
													</IconButton>
												)}
												{canManage && (
													<IconButton
														size="small"
														color="error"
														onClick={() => handleDeleteDelegacion(delegacion)}
														disabled={saving}
													>
														<DeleteOutlineIcon fontSize="small" />
													</IconButton>
												)}
											</Box>
										</Box>
									</Box>
								))}
							</Box>
						)}
					</Paper>
				</Grid>

				<Grid size={{ xs: 12, lg: 7 }}>
					<Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
						<Box
							sx={{
								px: 2,
								py: 1.5,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								gap: 1,
								bgcolor: "grey.50",
							}}
						>
							<Box>
								<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
									Clientes importados
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Consulta los clientes enlazados y filtra por campaña o delegación.
								</Typography>
							</Box>
							<Chip
								size="small"
								variant="outlined"
								label={`${filteredClientes.length}/${clientes.length} fila(s)`}
							/>
						</Box>

						<Box
							sx={{
								px: 2,
								py: 1.5,
								display: "flex",
								flexDirection: { xs: "column", md: "row" },
								gap: 1.5,
								borderTop: "1px solid",
								borderColor: "divider",
							}}
						>
							<FormControl fullWidth size="small">
								<InputLabel id="delegaciones-client-campaign-filter-label">
									Filtrar por campaña
								</InputLabel>
								<Select
									labelId="delegaciones-client-campaign-filter-label"
									label="Filtrar por campaña"
									value={clientCampaignFilter}
									onChange={(event) => setClientCampaignFilter(event.target.value)}
								>
									<MenuItem value="">
										<em>Todas</em>
									</MenuItem>
									{campaignFilterOptions.map((campana) => (
										<MenuItem
											key={`delegaciones-client-filter-campaign-${campana.id}`}
											value={String(campana.id)}
										>
											{campana.nombre}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<FormControl fullWidth size="small">
								<InputLabel id="delegaciones-client-delegacion-filter-label">
									Filtrar por delegación
								</InputLabel>
								<Select
									labelId="delegaciones-client-delegacion-filter-label"
									label="Filtrar por delegación"
									value={clientDelegacionFilter}
									onChange={(event) => setClientDelegacionFilter(event.target.value)}
								>
									<MenuItem value="">
										<em>Todas</em>
									</MenuItem>
									{delegacionFilterOptions.map((delegacion) => (
										<MenuItem
											key={`delegaciones-client-filter-delegacion-${delegacion.id}`}
											value={String(delegacion.id)}
										>
											{delegacion.nombre}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TextField
								fullWidth
								size="small"
								label="Buscar texto"
								placeholder="Cliente, referencia, rÃ©gimen..."
								value={clientSearch}
								onChange={(event) => setClientSearch(event.target.value)}
							/>
						</Box>

						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Cliente</TableCell>
									<TableCell>Referencia</TableCell>
									<TableCell>Delegación</TableCell>
									{/* <TableCell>Régimen</TableCell> */}
									<TableCell>Campaña</TableCell>
									{canManage && <TableCell align="right">Acciones</TableCell>}
								</TableRow>
							</TableHead>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={canManage ? 6 : 5}>
											<Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
												<CircularProgress size={18} />
											</Box>
										</TableCell>
									</TableRow>
								) : clientes.length === 0 ? (
									<TableRow>
										<TableCell colSpan={canManage ? 6 : 5}>
											<Typography variant="body2" color="text.secondary">
												No hay clientes importados todavía.
											</Typography>
										</TableCell>
									</TableRow>
								) : filteredClientes.length === 0 ? (
									<TableRow>
										<TableCell colSpan={canManage ? 6 : 5}>
											<Typography variant="body2" color="text.secondary">
												No hay clientes que coincidan con los filtros seleccionados.
											</Typography>
										</TableCell>
									</TableRow>
								) : (
									filteredClientes.map((cliente) => (
										<TableRow key={`delegaciones-cliente-${cliente.id}`} hover>
											<TableCell>{cliente.nombre || "-"}</TableCell>
											<TableCell>
												{cliente.ref_cliente_maestro || cliente.id_clienteG || "-"}
											</TableCell>
											<TableCell>{cliente.delegacion_nombre || "-"}</TableCell>
											{/* <TableCell>{cliente.regimen || "-"}</TableCell> */}
											<TableCell>{cliente.campana_nombre || "-"}</TableCell>
											{canManage && (
												<TableCell align="right">
													<Box
														sx={{
															display: "flex",
															justifyContent: "flex-end",
															gap: 0.5,
														}}
													>
														<IconButton
															size="small"
															onClick={() => openClienteEditor(cliente)}
															disabled={saving}
														>
															<EditOutlinedIcon fontSize="small" />
														</IconButton>
														<IconButton
															size="small"
															color="error"
															onClick={() => handleDeleteCliente(cliente)}
															disabled={saving}
														>
															<DeleteOutlineIcon fontSize="small" />
														</IconButton>
													</Box>
												</TableCell>
											)}
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</Paper>
				</Grid>
			</Grid>

			<Dialog open={delegacionDialogOpen} onClose={closeDelegacionDialog} fullWidth maxWidth="xs">
				<DialogTitle>Editar delegación</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 1 }}>
						<TextField
							fullWidth
							size="small"
							label="Nombre"
							value={delegacionForm.nombre}
							onChange={(event) =>
								setDelegacionForm((current) => ({
									...current,
									nombre: event.target.value,
								}))
							}
						/>
						{dialogError && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{dialogError}
							</Alert>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDelegacionDialog} disabled={saving}>
						Cancelar
					</Button>
					<Button variant="contained" onClick={handleSaveDelegacion} disabled={saving}>
						{saving ? "Guardando..." : "Guardar"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={clienteDialogOpen} onClose={closeClienteDialog} fullWidth maxWidth="sm">
				<DialogTitle>Editar cliente</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} sx={{ pt: 1 }}>
						<Grid size={{ xs: 12 }}>
							<TextField
								fullWidth
								size="small"
								label="Nombre"
								value={clienteForm.nombre}
								onChange={(event) =>
									setClienteForm((current) => ({
										...current,
										nombre: event.target.value,
									}))
								}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								size="small"
								label="Referencia maestro"
								value={clienteForm.ref_cliente_maestro}
								onChange={(event) =>
									setClienteForm((current) => ({
										...current,
										ref_cliente_maestro: event.target.value,
									}))
								}
							/>
						</Grid>
						{/* <Grid size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								size="small"
								label="Régimen"
								value={clienteForm.regimen}
								onChange={(event) =>
									setClienteForm((current) => ({
										...current,
										regimen: event.target.value,
									}))
								}
							/>
						</Grid> */}
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth size="small">
								<InputLabel id="delegaciones-edit-client-delegacion-label">
									Delegación
								</InputLabel>
								<Select
									labelId="delegaciones-edit-client-delegacion-label"
									label="Delegación"
									value={clienteForm.id_delegacion}
									onChange={(event) =>
										setClienteForm((current) => ({
											...current,
											id_delegacion: event.target.value,
										}))
									}
								>
									{delegacionFilterOptions.map((delegacion) => (
										<MenuItem
											key={`delegaciones-edit-client-delegacion-${delegacion.id}`}
											value={String(delegacion.id)}
										>
											{delegacion.nombre}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth size="small">
								<InputLabel id="delegaciones-edit-client-campaign-label">
									Campaña
								</InputLabel>
								<Select
									labelId="delegaciones-edit-client-campaign-label"
									label="Campaña"
									value={clienteForm.id_campana}
									onChange={(event) =>
										setClienteForm((current) => ({
											...current,
											id_campana: event.target.value,
										}))
									}
								>
									{campaignFilterOptions.map((campana) => (
										<MenuItem
											key={`delegaciones-edit-client-campana-${campana.id}`}
											value={String(campana.id)}
										>
											{campana.nombre}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						{dialogError && (
							<Grid size={{ xs: 12 }}>
								<Alert severity="error">{dialogError}</Alert>
							</Grid>
						)}
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeClienteDialog} disabled={saving}>
						Cancelar
					</Button>
					<Button variant="contained" onClick={handleSaveCliente} disabled={saving}>
						{saving ? "Guardando..." : "Guardar"}
					</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};
